import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import BottomNav from "@/components/event/BottomNav";
import TestModeBanner from "@/components/event/TestModeBanner";
import { eventApi } from "@/lib/eventHub";

/**
 * EventLayout — mobile-first shell for /event/* routes.
 * - Top-left: FIM logo (link home)
 * - Top-right: subtle "Sign in" text link to /admin (admins) or /volunteer/login (volunteers)
 * - Persistent bottom nav
 * - Sticky "Your program is always available here" banner near the top (per brief)
 */
export default function EventLayout({ children, showProgramBanner = true, hideBottomNav = false, className = "" }) {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const c = await eventApi.config();
        if (mounted) setConfig(c.data);
      } catch { /* noop */ }
      try {
        const a = await eventApi.announcements();
        if (mounted && a.data?.announcements?.length) setAnnouncement(a.data.announcements[0]);
      } catch { /* noop */ }
    })();
    eventApi.track("page_view", { path: location.pathname });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const banner = announcement?.body || config?.homepage_announcement;

  return (
    <div className={`min-h-screen bg-white text-emerald-950 ${className}`}>
      <TestModeBanner variant="public" />
      {/* Top header */}
      <header
        data-testid="event-header"
        className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-emerald-900/10"
      >
        <div className="mx-auto max-w-lg flex items-center justify-between px-4 py-3">
          <Link to="/event" data-testid="event-header-logo" className="flex items-center gap-2 group">
            <span className="h-9 w-9 rounded-xl bg-emerald-950 text-white flex items-center justify-center font-[Cormorant_Garamond] text-lg font-bold shadow-md shadow-fuchsia-500/10 group-hover:shadow-fuchsia-500/30 transition-shadow">
              F<span className="text-fuchsia-400">i</span>M
            </span>
            <div className="hidden sm:block leading-tight">
              <div className="font-[Cormorant_Garamond] text-base text-emerald-950">Business Showers</div>
              <div className="text-[9px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-700">Fast Forward · 2026</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-[11px] font-[Outfit]">
            <Link
              to="/event/volunteer-login"
              data-testid="event-header-volunteer"
              className="text-emerald-950/70 hover:text-fuchsia-700 uppercase tracking-wider font-semibold"
            >
              Volunteer
            </Link>
            <span className="text-emerald-900/20">·</span>
            <Link
              to="/admin"
              data-testid="event-header-admin"
              className="text-emerald-950/70 hover:text-fuchsia-700 uppercase tracking-wider font-semibold"
            >
              Admin
            </Link>
          </div>
        </div>

        {/* Announcement bar */}
        {banner && (
          <div
            data-testid="event-announcement-bar"
            className="bg-fuchsia-600 text-white text-xs sm:text-sm py-2 px-4 text-center font-[Outfit] font-medium"
          >
            {banner}
          </div>
        )}

        {/* "Your program is always available here" sticky reminder */}
        {showProgramBanner && (
          <Link
            to="/event/program"
            data-testid="event-program-banner"
            className="block bg-emerald-50 border-b border-emerald-100 text-center py-2 text-[12px] font-[Outfit] font-medium text-emerald-900 hover:bg-emerald-100 transition-colors"
          >
            Your program is always available here →
          </Link>
        )}
      </header>

      {/* Content */}
      <main data-testid="event-main">{children}</main>

      {/* Persistent legal footer (visible above the bottom nav) */}
      <footer className="mx-auto max-w-lg px-5 pb-4 pt-6 text-center print:hidden">
        <div className="text-[11px] font-[Outfit] text-emerald-950/50 space-x-3">
          <Link to="/privacy" className="hover:text-fuchsia-700">Privacy</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-fuchsia-700">Terms</Link>
          <span aria-hidden>·</span>
          <Link to="/photo-release" className="hover:text-fuchsia-700">Photo Release</Link>
        </div>
      </footer>

      {!hideBottomNav && <BottomNav />}
    </div>
  );
}
